/**
 * 店舗設定の健康診断 (v4.0.0)
 *
 * MATSURI の実データで分かったこと:
 *   - 原価・バック機能が ON なのに、130商品すべて原価が未入力
 *   - カテゴリ24件すべてに売上分配の設定が無い
 *   - 従業員11名のうち4名が表示名なし（選択リストに空欄が並ぶ）
 *   - 全員の給与形態が 'none'、時給も月給も未設定 → 給与が計算できない
 *   - バック率が 100%（既定値の入力ミスの可能性）
 *   - 売上スタッフが /staff と /employees に二重登録
 *
 * どれも「エラーにならないが、結果が空になる／おかしくなる」型で、
 * 使っている本人は「バグだらけ」としか言いようがない。
 * 設定が足りないことを、使う前に名指しで出す。
 */

export type Severity = 'blocker' | 'warning' | 'info';

export interface Diagnostic {
  id: string;
  severity: Severity;
  title: string;
  /** 何が起きるか */
  effect: string;
  /** どこで直すか */
  fix: string;
  /** 対象件数など */
  detail?: string;
}

export interface DiagnosticInput {
  shop: {
    timezone?: string;
    businessDayBoundaryTime?: number;
    salesPeriod?: { closingDay: number | 'monthEnd' } | null;
    taxRate?: number;
    priceDisplayType?: string;
    salesAttributionTaxBase?: string;
    costBackSettings?: { enabled?: boolean; defaultBackRate?: number } | null;
    incentiveSettings?: { enabled?: boolean; mode?: string; partTimeRules?: any } | null;
    paymentMethods?: { code: string; type: string; isActive?: boolean }[];
    receiptPrinterId?: string;
    mobileOrderEnabled?: boolean;
    attendanceSettings?: unknown;
  };
  employees: {
    id: string;
    displayName?: string;
    personalInfo?: { lastName?: string; firstName?: string };
    role?: string;
    status?: string;
    employmentInfo?: { paymentType?: string; hourlyWage?: number; monthlyWage?: number; dailyWage?: number };
    userId?: string;
  }[];
  /** レガシー /staff の件数。0 でなければ二重台帳 */
  legacyStaffCount: number;
  products: { id: string; name?: string; price?: number; categoryId?: string; costInfo?: { cost?: number } | null }[];
  categories: { id: string; name?: string; salesDistribution?: unknown }[];
  tables: { id: string }[];
  printers: { id: string }[];
}

export function runSetupDiagnostics(input: DiagnosticInput): Diagnostic[] {
  const out: Diagnostic[] = [];
  const { shop, employees, products, categories, tables, printers, legacyStaffCount } = input;
  const active = employees.filter((e) => e.status === 'active');

  // ---- 二重台帳 ----
  if (legacyStaffCount > 0) {
    out.push({
      id: 'legacy-staff',
      severity: 'blocker',
      title: '売上スタッフの台帳が2つある',
      effect:
        '会計で選ぶスタッフと、勤怠・シフト・給与で使うスタッフが別の ID になり、' +
        '同じ人として集計できません。累進バックの出勤日数が常に 0 になります。',
      fix: '下の「統合する」から実行してください。名前で突き合わせ、古い記録は消さずに残します。',
      detail: `未統合のレガシー /staff が ${legacyStaffCount} 件`,
    });
  }

  // ---- 従業員 ----
  const unnamed = active.filter((e) => {
    const full = [e.personalInfo?.lastName, e.personalInfo?.firstName].filter(Boolean).join('').trim();
    return !e.displayName?.trim() && !full;
  });
  if (unnamed.length > 0) {
    out.push({
      id: 'employee-no-name',
      severity: 'blocker',
      title: '名前が入っていない従業員がいる',
      effect: '会計の売上スタッフ選択やシフト表に空欄が並び、誰を選んでいるか分かりません。',
      fix: '従業員管理から表示名を入れてください。',
      detail: `${unnamed.length} 名（在籍 ${active.length} 名中）`,
    });
  }

  const noWage = active.filter((e) => {
    const i = e.employmentInfo;
    if (e.role === 'owner') return false;
    if (!i?.paymentType || i.paymentType === 'none') return true;
    if (i.paymentType === 'hourly') return !(i.hourlyWage! > 0);
    if (i.paymentType === 'monthly') return !(i.monthlyWage! > 0);
    if (i.paymentType === 'daily') return !(i.dailyWage! > 0);
    return true;
  });
  if (noWage.length > 0) {
    out.push({
      id: 'employee-no-wage',
      severity: 'blocker',
      title: '給与形態・賃金が未設定の従業員がいる',
      effect:
        '給与が計算できません。累進バックの「時給未満」判定もできないため、' +
        'アルバイトのレートが最低ティアに固定されます。',
      fix: '従業員管理 → 雇用情報 で、給与形態（時給／日給／月給）と金額を入れてください。',
      detail: `${noWage.length} 名`,
    });
  }

  const noLogin = active.filter((e) => !e.userId && e.role !== 'owner');
  if (noLogin.length > 0) {
    out.push({
      id: 'employee-no-login',
      severity: 'info',
      title: 'ログインを持たない従業員がいる',
      effect: 'この人たちはアプリを使えません（PC のみ運用なら問題ありません）。',
      fix: 'アプリを使わせる場合は従業員管理からアカウントを作成してください。',
      detail: `${noLogin.length} 名`,
    });
  }

  // ---- 原価・バック ----
  if (shop.costBackSettings?.enabled) {
    const withCost = products.filter((p) => (p.costInfo?.cost ?? 0) > 0);
    if (withCost.length === 0 && products.length > 0) {
      out.push({
        id: 'no-cost',
        severity: 'blocker',
        title: '原価・バック機能が有効なのに、原価が1件も入っていない',
        effect:
          'バック額は（販売価格 − 原価）× バック率で計算されるため、原価が無いと' +
          '販売価格の全額がバックの母数になります。利益が残りません。',
        fix: '商品管理 → 各商品の「原価・コミッション設定」で原価を入れてください。',
        detail: `商品 ${products.length} 件すべて原価なし`,
      });
    } else if (withCost.length < products.length) {
      out.push({
        id: 'partial-cost',
        severity: 'warning',
        title: '原価が入っていない商品がある',
        effect: 'その商品だけバックの母数が販売価格の全額になります。',
        fix: '商品管理から原価を入れてください。',
        detail: `${products.length - withCost.length} 件 / ${products.length} 件`,
      });
    }

    const rate = shop.costBackSettings.defaultBackRate;
    if (rate != null && rate >= 1) {
      out.push({
        id: 'backrate-100',
        severity: 'warning',
        title: `既定のバック率が ${Math.round(rate * 100)}% になっている`,
        effect:
          '粗利の全額がスタッフへのバックになります。意図した設定でなければ、' +
          '売上が立つほど店の取り分が無くなります。',
        fix: '店舗設定 → 原価・バック機能 でバック率を見直してください。',
      });
    }
  }

  const noDist = categories.filter((c) => !c.salesDistribution);
  if (shop.incentiveSettings?.enabled && noDist.length === categories.length && categories.length > 0) {
    out.push({
      id: 'no-category-distribution',
      severity: 'warning',
      title: 'カテゴリの売上分配設定が1件も無い',
      effect:
        '全カテゴリが「原価を引かない・利益控除0%」で扱われます。' +
        'シャンパンやボトルだけ扱いを変えたい場合は設定が必要です。',
      fix: '商品カテゴリ管理 → 各カテゴリの売上分配設定。',
      detail: `カテゴリ ${categories.length} 件`,
    });
  }

  if (shop.incentiveSettings?.enabled && !shop.incentiveSettings.mode) {
    out.push({
      id: 'no-progressive-mode',
      severity: 'warning',
      title: '累進バックの計算方式が未設定',
      effect:
        '既定（累計全体に当てる）で動きます。旧方式のままにすると、' +
        '同じ月商でも会計を分けるほどバックが減ります。',
      fix: '店舗設定 → インセンティブ設定 → 累進の当て方。',
    });
  }

  if (shop.incentiveSettings?.enabled && !shop.incentiveSettings.partTimeRules?.standardDailyHours) {
    out.push({
      id: 'no-standard-hours',
      severity: 'info',
      title: '「時給未満」判定の想定勤務時間が未設定',
      effect: '既定の 6 時間で判定します。実働が違う場合はレートがずれます。',
      fix: '店舗設定 → インセンティブ設定 → 1日の想定勤務時間。',
    });
  }

  // ---- 営業日・締日 ----
  if (shop.businessDayBoundaryTime == null) {
    out.push({
      id: 'no-boundary',
      severity: 'warning',
      title: '営業日の区切り時刻が未設定',
      effect:
        '深夜0時で日付が変わります。0時をまたいで営業する店では、' +
        '同じ一夜の売上と勤怠が2日に分かれます。',
      fix: '店舗設定 → 基本情報 → 営業日区切り時刻。',
    });
  }
  if (!shop.salesPeriod) {
    out.push({
      id: 'no-closing-day',
      severity: 'info',
      title: '売上の締日が未設定',
      effect: '月末締めとして扱います。',
      fix: '店舗設定 → 売上集計・分配の店舗ポリシー → 売上の締日。',
    });
  }
  if (!shop.attendanceSettings) {
    out.push({
      id: 'no-attendance-settings',
      severity: 'info',
      title: '遅刻・早退の猶予が未設定',
      effect: '既定（遅刻5分・早退15分）で判定します。',
      fix: '店舗設定 → 勤怠の判定。',
    });
  }

  // ---- 運用に必要なもの ----
  // 件数が0のときは「違反0件」ではなく「まだ始められない」。
  // 0件を素通りさせると、何も入っていない店が緑で出る。
  if (products.length === 0) {
    out.push({
      id: 'no-products',
      severity: 'blocker',
      title: '商品が1つも登録されていない',
      effect: '注文を取れません。伝票も会計も作れません。',
      fix: '商品管理から登録してください。',
    });
  }
  if (categories.length === 0) {
    out.push({
      id: 'no-categories',
      severity: 'blocker',
      title: '商品カテゴリが1つも無い',
      effect: '商品を登録できず、原価とバックの設定もカテゴリ単位でできません。',
      fix: '商品カテゴリ管理から登録してください。',
    });
  }
  // 卓はモバイルオーダーを使わなくても要る。伝票を卓に紐づけるため。
  if (tables.length === 0) {
    out.push({
      id: 'no-tables',
      severity: 'blocker',
      title: '卓が1つも登録されていない',
      effect: shop.mobileOrderEnabled
        ? 'QRコードを発行できず、卓に注文を紐づけられません。'
        : '伝票を卓に紐づけられず、複数卓を同時に回せません。',
      fix: 'テーブル管理から卓を登録してください。',
    });
  }
  // オーナー1人だけの状態。売上の割り当て先が無い。
  const workingEmployees = employees.filter(
    (e) => e.status !== 'retired' && e.role !== 'owner'
  );
  if (workingEmployees.length === 0) {
    out.push({
      id: 'no-employees',
      severity: 'warning',
      title: 'オーナー以外の従業員が登録されていない',
      effect: '指名・同伴・バックの割り当て先がありません。1人で回す店なら問題ありません。',
      fix: '従業員管理から登録してください。',
    });
  }
  if (printers.length === 0 && shop.mobileOrderEnabled) {
    out.push({
      id: 'no-printer',
      severity: 'info',
      title: 'プリンターが未設定',
      effect: 'キッチン伝票とレシートが印刷されません。画面での確認のみになります。',
      fix: 'プリンター管理から登録してください（使わない運用なら問題ありません）。',
    });
  }
  const activePayments = (shop.paymentMethods ?? []).filter((m) => m.isActive !== false);
  if (activePayments.length === 0) {
    out.push({
      id: 'no-payment-method',
      severity: 'blocker',
      title: '支払い方法が1つも有効になっていない',
      effect: '会計画面で支払い方法を選べません。',
      fix: '店舗設定 → 支払い方法。',
    });
  }
  const noPriceProducts = products.filter((p) => !(p.price! > 0));
  if (noPriceProducts.length > 0) {
    out.push({
      id: 'no-price',
      severity: 'warning',
      title: '価格が入っていない商品がある',
      effect: '注文しても金額が 0 円で計上されます。',
      fix: '商品管理から価格を入れてください。',
      detail: `${noPriceProducts.length} 件`,
    });
  }
  const noCategory = products.filter((p) => !p.categoryId);
  if (noCategory.length > 0) {
    out.push({
      id: 'no-category',
      severity: 'warning',
      title: 'カテゴリ未設定の商品がある',
      effect: '売上分配の設定が効かず、メニューにも出ません。',
      fix: '商品管理からカテゴリを設定してください。',
      detail: `${noCategory.length} 件`,
    });
  }

  const order: Record<Severity, number> = { blocker: 0, warning: 1, info: 2 };
  return out.sort((a, b) => order[a.severity] - order[b.severity]);
}

export const SEVERITY_LABELS: Record<Severity, string> = {
  blocker: '使えない',
  warning: '確認が必要',
  info: '任意',
};
